import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { sipConfigApi } from '../lib/api';
import { toast } from 'sonner';

export const SIP_CALL_STATUS = {
  IDLE: 'idle',
  REGISTERING: 'registering',
  REGISTERED: 'registered',
  DIALING: 'dialing',
  RINGING: 'ringing',
  INCOMING: 'incoming',
  CONNECTED: 'connected',
  FAILED: 'failed',
};

const SIPContext = createContext(null);

export function useSIP() {
  return useContext(SIPContext);
}

export function SIPProvider({ children }) {
  const { user } = useAuth();
  const [sipConfig, setSipConfig] = useState(null);
  const [sipEnabled, setSipEnabled] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(SIP_CALL_STATUS.IDLE);
  const [callStatus, setCallStatus] = useState(SIP_CALL_STATUS.IDLE);
  const [currentCall, setCurrentCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [externalCallLogs, setExternalCallLogs] = useState([]);

  const clientRef = useRef(null); // TelnyxRTC client or SIP.js UserAgent
  const registererRef = useRef(null); // SIP.js Registerer (only for SIP.js mode)
  const activeCallRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);
  const callDurationRef = useRef(0);
  const callStartRef = useRef(null);
  const modeRef = useRef(null); // 'telnyx' or 'sipjs'

  useEffect(() => {
    if (!user) return;
    loadConfig();
  }, [user]);

  const loadConfig = async () => {
    try {
      const config = await sipConfigApi.getConfig();
      setSipConfig(config);
      setSipEnabled(config?.enabled || false);
    } catch {
      setSipConfig(null);
      setSipEnabled(false);
    }
  };

  useEffect(() => {
    if (sipEnabled && sipConfig?.sip_username && sipConfig?.sip_password) {
      connect();
    } else {
      disconnect();
    }
    return () => disconnect();
  }, [sipEnabled, sipConfig?.sip_username, sipConfig?.sip_password, sipConfig?.wss_url]);

  const logExternalCall = async (logEntry) => {
    try { await sipConfigApi.logExternalCall(logEntry); } catch {}
  };

  const endCall = useCallback((log = true) => {
    const call = activeCallRef.current;
    const callInfo = currentCall;
    const duration = callDurationRef.current;

    // IMMEDIATELY clear the active call to prevent synchronous infinite loops
    // if Telnyx SDK fires recursive 'destroy' or 'hangup' notifications
    activeCallRef.current = null;

    // Immediately clear local state so React unmounts the call UI 
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    setCallStatus(SIP_CALL_STATUS.IDLE);
    setCurrentCall(null);
    setCallDuration(0);
    setIsMuted(false);
    callDurationRef.current = 0;

    // Handle SDK hangup and logging asynchronously
    const cleanupCall = async () => {
      if (call) {
        try {
          if (modeRef.current === 'telnyx') {
            // Only hangup if not already destroyed
            if (call.state !== 'destroy' && call.state !== 'hangup') {
              for (let i = 0; i < 2; i++) {
                try {
                  await call.hangup();
                  break; // Success
                } catch (error) {
                  if (error?.code === -32002) {
                    // Call already gone, stop retrying
                    break;
                  }
                  if (i === 1) console.error('Telnyx hangup failed:', error);
                  await new Promise(resolve => setTimeout(resolve, 100));
                }
              }
            }
          } else {
            // SIP.js session
            try { call.bye(); } catch { try { call.cancel(); } catch {} }
          }
        } catch (err) {
          console.error('Error during endCall cleanup:', err);
        }
      }

      if (log && callInfo) {
        const logEntry = {
          number: callInfo.number,
          displayName: callInfo.displayName,
          direction: callInfo.direction,
          duration,
          timestamp: callStartRef.current?.toISOString() || new Date().toISOString(),
          status: duration > 0 ? 'completed' : 'missed',
        };
        setExternalCallLogs(prev => [logEntry, ...prev].slice(0, 50));
        logExternalCall(logEntry);
      }
    };

    cleanupCall();
  }, [currentCall]);

  // ═══════════════════════════════════════
  // Auto-detect mode: Telnyx vs SIP.js
  // ═══════════════════════════════════════
  const isTelnyxMode = () => {
    if (!sipConfig?.wss_url) return true; // No WSS = use Telnyx
    const wss = sipConfig.wss_url.toLowerCase();
    return wss.includes('telnyx');
  };

  const connect = useCallback(async () => {
    if (!sipConfig) return;
    disconnect();

    if (isTelnyxMode()) {
      await connectTelnyx();
    } else {
      await connectSIPjs();
    }
  }, [sipConfig]);

  // ═══════════════════════════════════════
  // TELNYX MODE
  // ═══════════════════════════════════════
  const connectTelnyx = async () => {
    try {
      const { TelnyxRTC } = await import('@telnyx/webrtc');
      setRegistrationStatus(SIP_CALL_STATUS.REGISTERING);
      modeRef.current = 'telnyx';

      const client = new TelnyxRTC({
        login: sipConfig.sip_username,
        password: sipConfig.sip_password,
      });

      client.on('telnyx.ready', () => setRegistrationStatus(SIP_CALL_STATUS.REGISTERED));
      client.on('telnyx.error', () => setRegistrationStatus(SIP_CALL_STATUS.FAILED));
      client.on('telnyx.socket.close', () => setRegistrationStatus(SIP_CALL_STATUS.IDLE));
      client.on('telnyx.notification', (n) => handleTelnyxNotification(n));

      client.connect();
      clientRef.current = client;
    } catch (err) {
      console.warn('Telnyx connect error:', err);
      setRegistrationStatus(SIP_CALL_STATUS.FAILED);
    }
  };

  const handleTelnyxNotification = (notification) => {
    const call = notification.call;
    if (!call) return;
    if (notification.type !== 'callUpdate') return;

    const state = call.state;
    switch (state) {
      case 'new': case 'trying':
        setCallStatus(SIP_CALL_STATUS.DIALING); break;
      case 'early': case 'ringing':
        if (call.direction === 'inbound' && callStatus !== SIP_CALL_STATUS.CONNECTED) {
          setCallStatus(SIP_CALL_STATUS.INCOMING);
          const caller = call.options?.remoteCallerNumber || 'Unknown';
          setCurrentCall({ number: caller, displayName: call.options?.remoteCallerName || caller, direction: 'incoming' });
          activeCallRef.current = call;
          toast.info(`Incoming call from ${caller}`, { duration: 10000 });
        } else {
          setCallStatus(SIP_CALL_STATUS.RINGING);
        }
        break;
      case 'active':
        setCallStatus(SIP_CALL_STATUS.CONNECTED);
        if (call.remoteStream && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = call.remoteStream;
        }
        startCallTimer();
        break;
      case 'hangup': case 'destroy':
        endCall(true); break;
      default: break;
    }
  };

  // ═══════════════════════════════════════
  // SIP.JS MODE (FreeSWITCH / Generic)
  // ═══════════════════════════════════════
  const connectSIPjs = async () => {
    if (!sipConfig.wss_url?.startsWith('ws')) return;

    try {
      const SIPjs = await import('sip.js');
      const { UserAgent, Registerer, SessionState, RegistererState } = SIPjs;
      setRegistrationStatus(SIP_CALL_STATUS.REGISTERING);
      modeRef.current = 'sipjs';

      const domain = sipConfig.sip_domain || sipConfig.wss_url.replace(/^wss?:\/\//, '').replace(/:\d+$/, '');
      const uri = UserAgent.makeURI(`sip:${sipConfig.sip_username}@${domain}`);
      if (!uri) {
        setRegistrationStatus(SIP_CALL_STATUS.FAILED);
        return;
      }

      const ua = new UserAgent({
        uri,
        transportOptions: { server: sipConfig.wss_url, connectionTimeout: 15 },
        authorizationUsername: sipConfig.sip_username,
        authorizationPassword: sipConfig.sip_password,
        displayName: sipConfig.display_name || user?.name || 'EMS',
        logLevel: 'warn',
        delegate: {
          onInvite: (invitation) => {
            const caller = invitation.remoteIdentity?.uri?.user || 'Unknown';
            setCurrentCall({ number: caller, displayName: invitation.remoteIdentity?.displayName || caller, direction: 'incoming' });
            setCallStatus(SIP_CALL_STATUS.INCOMING);
            activeCallRef.current = invitation;
            invitation.stateChange.addListener((s) => {
              if (s === SessionState.Terminated) endCall(true);
            });
            toast.info(`Incoming call from ${caller}`, { duration: 10000 });
          },
        },
      });

      await ua.start();
      clientRef.current = ua;

      const registerer = new Registerer(ua, { expires: 120 });
      registererRef.current = registerer;

      registerer.stateChange.addListener((state) => {
        if (state === RegistererState.Registered) setRegistrationStatus(SIP_CALL_STATUS.REGISTERED);
        else if (state === RegistererState.Unregistered || state === RegistererState.Terminated) setRegistrationStatus(SIP_CALL_STATUS.IDLE);
      });

      await registerer.register();
    } catch (err) {
      console.warn('SIP.js connect error:', err.message || err);
      setRegistrationStatus(SIP_CALL_STATUS.FAILED);
    }
  };

  // ═══════════════════════════════════════
  // DISCONNECT (both modes)
  // ═══════════════════════════════════════
  const disconnect = useCallback(() => {
    if (activeCallRef.current) {
      try {
        if (modeRef.current === 'telnyx') activeCallRef.current.hangup();
        else { try { activeCallRef.current.bye(); } catch { try { activeCallRef.current.cancel(); } catch {} } }
      } catch {}
      activeCallRef.current = null;
    }
    if (registererRef.current) {
      try { registererRef.current.unregister(); } catch {}
      registererRef.current = null;
    }
    if (clientRef.current) {
      try {
        if (modeRef.current === 'telnyx') clientRef.current.disconnect();
        else clientRef.current.stop();
      } catch {}
      clientRef.current = null;
    }
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    modeRef.current = null;
    setRegistrationStatus(SIP_CALL_STATUS.IDLE);
    setCallStatus(SIP_CALL_STATUS.IDLE);
    setCurrentCall(null);
    setCallDuration(0);
    setIsMuted(false);
  }, []);

  // ═══════════════════════════════════════
  // MAKE CALL (both modes)
  // ═══════════════════════════════════════
  const makeCall = useCallback(async (number) => {
    if (callStatus !== SIP_CALL_STATUS.IDLE) return;

    // Request mic permission
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
    } catch (micErr) {
      toast.error(micErr.name === 'NotAllowedError' ? 'Microphone access denied' : 'No microphone found');
      return;
    }

    let cleanNumber = number.replace(/[^0-9+*#]/g, '');
    if (!cleanNumber) { toast.error('Invalid phone number'); return; }

    setCallStatus(SIP_CALL_STATUS.DIALING);
    setCurrentCall({ number: cleanNumber, displayName: cleanNumber, direction: 'outgoing' });
    callStartRef.current = new Date();
    toast.info(`Calling ${cleanNumber}...`);

    if (modeRef.current === 'telnyx') {
      try {
        const call = clientRef.current.newCall({
          destinationNumber: cleanNumber,
          callerNumber: sipConfig?.caller_id || '',
        });
        activeCallRef.current = call;
      } catch (err) {
        toast.error(`Call failed: ${err.message}`);
        setCallStatus(SIP_CALL_STATUS.IDLE);
        setCurrentCall(null);
      }
    } else {
      // SIP.js mode
      try {
        const SIPjs = await import('sip.js');
        const { Inviter, SessionState } = SIPjs;
        const domain = sipConfig.sip_domain || sipConfig.wss_url.replace(/^wss?:\/\//, '').replace(/:\d+$/, '');
        const targetURI = SIPjs.UserAgent.makeURI(`sip:${cleanNumber}@${domain}`);
        if (!targetURI) { toast.error('Invalid number'); setCallStatus(SIP_CALL_STATUS.IDLE); setCurrentCall(null); return; }

        const inviter = new Inviter(clientRef.current, targetURI, {
          sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
        });
        activeCallRef.current = inviter;

        inviter.stateChange.addListener((state) => {
          switch (state) {
            case SessionState.Establishing: setCallStatus(SIP_CALL_STATUS.RINGING); break;
            case SessionState.Established:
              setCallStatus(SIP_CALL_STATUS.CONNECTED);
              try {
                const pc = inviter.sessionDescriptionHandler?.peerConnection;
                if (pc && remoteAudioRef.current) {
                  const rs = new MediaStream();
                  pc.getReceivers().forEach(r => { if (r.track) rs.addTrack(r.track); });
                  remoteAudioRef.current.srcObject = rs;
                }
              } catch {}
              startCallTimer();
              toast.success('Call connected');
              break;
            case SessionState.Terminated: endCall(true); break;
            default: break;
          }
        });

        await inviter.invite();
      } catch (err) {
        console.error('SIP.js call error:', err);
        toast.error(`Call failed: ${err.message || 'Unknown error'}`);
        setCallStatus(SIP_CALL_STATUS.IDLE);
        setCurrentCall(null);
      }
    }
  }, [callStatus, sipConfig, endCall]);

  // ═══════════════════════════════════════
  // ANSWER / REJECT (both modes)
  // ═══════════════════════════════════════
  const answerCall = useCallback(async () => {
    const call = activeCallRef.current;
    if (!call) return;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true }).then(s => s.getTracks().forEach(t => t.stop()));
    } catch { toast.error('Microphone access required'); return; }

    try {
      if (modeRef.current === 'telnyx') {
        call.answer();
      } else {
        await call.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } } });
      }
      callStartRef.current = new Date();
    } catch (err) {
      toast.error('Failed to answer call');
    }
  }, []);

  const rejectCall = useCallback(() => {
    const call = activeCallRef.current;
    if (!call) return;
    try {
      if (modeRef.current === 'telnyx') call.hangup();
      else { try { call.reject(); } catch { try { call.bye(); } catch {} } }
    } catch {}
    activeCallRef.current = null;
    setCallStatus(SIP_CALL_STATUS.IDLE);
    setCurrentCall(null);
  }, []);

  // ═══════════════════════════════════════
  // MUTE / DTMF / TIMER
  // ═══════════════════════════════════════
  const toggleMute = () => {
    const call = activeCallRef.current;
    if (!call) return;
    try {
      if (modeRef.current === 'telnyx') {
        isMuted ? call.unmuteAudio() : call.muteAudio();
      } else {
        const pc = call.sessionDescriptionHandler?.peerConnection;
        if (pc) pc.getSenders().forEach(s => { if (s.track?.kind === 'audio') s.track.enabled = isMuted; });
      }
      setIsMuted(prev => !prev);
    } catch {}
  };

  const sendDTMF = useCallback((tone) => {
    const call = activeCallRef.current;
    if (!call) return;
    try {
      if (modeRef.current === 'telnyx') call.dtmf(tone);
      else {
        const pc = call.sessionDescriptionHandler?.peerConnection;
        const sender = pc?.getSenders().find(s => s.track?.kind === 'audio');
        if (sender?.dtmf) sender.dtmf.insertDTMF(tone, 100, 70);
      }
    } catch {}
  }, []);

  const startCallTimer = () => {
    setCallDuration(0);
    callDurationRef.current = 0;
    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => { const n = prev + 1; callDurationRef.current = n; return n; });
    }, 1000);
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const value = {
    sipConfig, sipEnabled, registrationStatus, callStatus, currentCall,
    callDuration, isMuted, externalCallLogs, remoteAudioRef,
    setSipEnabled, loadConfig, makeCall, answerCall, rejectCall,
    endCall, toggleMute, sendDTMF, formatDuration,
  };

  return (
    <SIPContext.Provider value={value}>
      {children}
      <audio ref={remoteAudioRef} autoPlay />
    </SIPContext.Provider>
  );
}
